import styled from '@emotion/styled'
import { ClientSideDelivery, DeliveryType } from '@pubstack/common/src/clientSideDelivery'
import { DateTime } from 'luxon'
import { FunctionComponent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { useClientSideDelivery, useCreateClientSideDelivery, useGetDeployedFiles } from '~/api/admin/client-side-delivery.api'
import Button from '~/components/Button'
import { Input } from '~/components/Input'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Skeleton } from '~/components/Skeleton'
import Table, { Column, EmptyTable } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'

const Wrapper = styled.div`
  margin: 20px;
`

const NewConfigForm = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`

const H3WithMargin = styled.h3`
  margin-top: 10px;
`

const TableConfigs: FunctionComponent<{ configs: ClientSideDelivery[] }> = ({ configs }) => {
  return configs.length > 0 ? (
    configs
      .sort((a, b) => b.timestamp - a.timestamp)
      .map((c) => (
        <TableRow key={c.timestamp}>
          <TableCell>{DateTime.fromSeconds(c.timestamp).toFormat('yyyy-LL-dd HH:mm:ss')}</TableCell>
          <TableCell>{c.mainName}</TableCell>
          <TableCell>{c.abTest?.newName ?? ''}</TableCell>
          <TableCell>{c.abTest?.ratio ?? 0}%</TableCell>
        </TableRow>
      ))
  ) : (
    <TableRow>
      <TableCell colspan={5}>
        <EmptyTable>
          <div>
            <p>No history for this component</p>
          </div>
        </EmptyTable>
      </TableCell>
    </TableRow>
  )
}

const COMPONENTS: SelectOptionProp[] = [
  { value: 'collector', label: 'Collector', trackingLabel: 'common-admin-delivery-collector-set' },
  { value: 'index-monitoring', label: 'Monitoring', trackingLabel: 'common-admin-delivery-index-monitoring-set' },
  { value: 'user-sessions', label: 'User Sessions', trackingLabel: 'common-admin-delivery-user-sessions-set' },
  { value: 'kleanads', label: 'Kleanads.js', trackingLabel: 'common-admin-delivery-kleanads-set' },
]
export type ConfigFormValues = { mainScript: string; newScript: string; ratio: number; currentComponent: string }

export const AdminDelivery: FunctionComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams({})
  const [currentComponent, setCurrentComponent] = useState<string>('')
  const { data: scripts, isFetching: isFetchingDeployedFiles } = useGetDeployedFiles(currentComponent)
  const { data: config, isFetching: isFetchingClientSideDelivery } = useClientSideDelivery(currentComponent)
  const { mutateAsync: createConfigDelivery } = useCreateClientSideDelivery({
    successToast: 'Configuration creation successful',
    errorToast: 'An error occurred when creating the newest configuration',
  })

  const { control, handleSubmit } = useForm<ConfigFormValues>({
    defaultValues: {
      currentComponent: currentComponent,
      mainScript: '',
      newScript: '',
      ratio: 0,
    },
  })
  const onSubmit = async (formValues: ConfigFormValues) => {
    await createConfigDelivery({
      component: currentComponent as DeliveryType,
      mainName: formValues.mainScript,
      abTest: {
        newName: scripts.includes(formValues.newScript) ? formValues.newScript : '',
        ratio: formValues.newScript ? formValues.ratio : 0,
      },
    })
  }
  const mainScriptsOptions: SelectOptionProp[] = scripts.map((s) => ({ value: s, label: s, trackingLabel: 'common-admin-delivery-main-version-set' }))
  const newScriptsOptions: SelectOptionProp[] = ['', ...scripts].map((s) => ({ value: s, label: s.length ? s : 'N/A', trackingLabel: 'common-admin-delivery-new-version-set' }))

  const COLUMNS: Column[] = [
    {
      name: 'Creation date',
      isSortable: false,
    },
    {
      name: 'Main tag',
      isSortable: false,
    },
    {
      name: 'New tag',
      isSortable: false,
    },
    {
      name: 'Ratio',
      isSortable: false,
    },
  ]

  useEffect(() => {
    const component = searchParams.get('component')
    if (component) {
      // Initialises the selected component from the URL on mount.
      // oxlint-disable-next-line react/set-state-in-effect
      setCurrentComponent(component)
    }
  }, [])

  const onComponentChange = (newComponent: SelectOptionProp) => {
    setSearchParams({ component: newComponent.value as string })
    setCurrentComponent(newComponent.value as string)
  }

  return isFetchingDeployedFiles || isFetchingClientSideDelivery ? (
    <Skeleton bigger width={'100%'} />
  ) : (
    <Wrapper>
      <NewConfigForm>
        <p>Create a new delivery config with A/B test (optional)</p>
        <Select control={control} name={'currentComponent'} label={'Component'} options={COMPONENTS} onChange={onComponentChange} />
        {currentComponent && (
          <>
            <Select control={control} name={'mainScript'} label={'Main script'} options={mainScriptsOptions} />
            <Select control={control} name={'newScript'} label={'New script'} options={newScriptsOptions} />
            <Input min={0} max={100} type={'number'} control={control} name={'ratio'} label={'Ratio'} />
            <Button onClick={handleSubmit(onSubmit)} trackingLabel={'common-admin-delivery-save'}>
              Create config
            </Button>
          </>
        )}
      </NewConfigForm>
      {currentComponent && (
        <>
          <h3>Bootstrap {currentComponent} current configuration</h3>
          <Table isLoading={false} columns={COLUMNS}>
            {config?.currentConfig ? (
              <TableRow>
                <TableCell>{DateTime.fromSeconds(config?.currentConfig.timestamp).toFormat('yyyy-LL-dd HH:mm:ss')}</TableCell>
                <TableCell>{config?.currentConfig.mainName}</TableCell>
                <TableCell>{config?.currentConfig.abTest?.newName ?? ''}</TableCell>
                <TableCell>{config?.currentConfig.abTest?.ratio ?? 0}%</TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell colspan={5}>
                  <EmptyTable>
                    <div>
                      <h3>Something happened</h3>
                      <p>An error occurred when getting current configuration.</p>
                    </div>
                    <Button onClick={() => {}} trackingLabel={'common-admin-delivery-refresh'}>
                      Try again
                    </Button>
                  </EmptyTable>
                </TableCell>
              </TableRow>
            )}
          </Table>
          {config?.allConfig && (
            <>
              <H3WithMargin>Bootstrap {currentComponent} configuration history</H3WithMargin>
              <Table isLoading={false} columns={COLUMNS}>
                <TableConfigs configs={config?.allConfig} />
              </Table>
            </>
          )}
        </>
      )}
    </Wrapper>
  )
}
